/**
 * Умова й сортування подання для запиту даних (views.md, розд. 6): сервер конфігурації подання не
 * підставляє, тож клієнт шле її сам у `POST …/query`. Джерело — `GET /api/v1/views/{id}`, а для
 * стартового подання — `meta.start.config` (без другого запиту). Подання платформи й компанії сталі
 * протягом сесії, тож їх кеш — на сесію вкладки. Подання людей змінює й інший редактор спільного
 * подання (`views.edit_shared`), тож їх кеш свіжий 5 хв, як у списків (рев'ю Архітектора #842,
 * Should 2); власні зміни view-api.ts кладе в кеш одразу.
 *
 * Один ключ для списку (ListPage) і «Моїх задач» (features/tasks/my): дані під ним — завжди
 * `ViewQueryConfig`, а повне подання з редакцією (view-api.ts) — під батьківським ключем.
 *
 * Модуль — у першому екрані (його читає `loader` списку): без UI.
 */
import type {
  AppMeta,
  FilterNode,
  MetaView,
  ViewConfig,
  ViewOwnerKind,
  ViewSort,
} from '@clientline/contracts';
import { type QueryClient, queryOptions } from '@tanstack/react-query';
import { api, retryRead } from './api.ts';
import { queryPolicy } from './query-policy.ts';

export interface ViewQueryConfig {
  filter: FilterNode | undefined;
  sort: ViewSort[];
}

/** Подання людині недоступне (поле умови приховане роллю, views.md, розд. 3). */
export const VIEW_UNAVAILABLE = 'view_unavailable';

/** Під ключем повного подання (`viewKey`): його видалення прибирає й конфігурацію. */
export const viewConfigKey = (id: string) => ['p', 'view', id, 'config'] as const;

export const configOf = (config: Pick<ViewConfig, 'filter' | 'sort'>): ViewQueryConfig => ({
  // `null` контракт не дає, але старий сервер і фікстури слали: «без умови» — лише `undefined`.
  filter: config.filter ?? undefined,
  sort: config.sort.map((s) => ({ field: s.field, dir: s.dir })),
});

/** Конфігурація подання для дошки (views.md, розд. 15): умова, сортування й вигляд `board`. */
export interface BoardQueryConfig extends ViewQueryConfig {
  /** Етапи, згорнуті за замовчуванням у поданні (KAN-03, значення компанії). */
  collapsed: readonly string[];
  /** Воронка подання за замовчуванням. */
  pipeline: string | undefined;
  /**
   * Поля картки подання (KAN-08, `board.cardFields`): сервер уже прибрав приховані роллю. Немає —
   * склад за замовчуванням (`layouts.compact` сутності).
   */
  cardFields?: readonly string[] | undefined;
}

/**
 * Ключ конфігурації дошки — теж під ключем повного подання: «Зберегти» з дошки (view-api.ts) кладе
 * сюди нову редакцію, видалення подання прибирає й її.
 */
export const boardConfigKey = (id: string) => ['p', 'view', id, 'board'] as const;

export const boardConfigOf = (
  config: Pick<ViewConfig, 'filter' | 'sort' | 'board'>,
): BoardQueryConfig => ({
  ...configOf(config),
  collapsed: config.board?.collapsed ?? [],
  pipeline: config.board?.pipeline,
  cardFields: config.board?.cardFields,
});

/**
 * Свіжість конфігурації подання за власником — спільна для списку й дошки (board-data.ts):
 * подання платформи й компанії незмінні за сесію, подання людини (або невідомого власника) — як
 * списки (`query-policy.ts`).
 */
export const viewConfigStaleTime = (ownerKind: ViewOwnerKind | undefined) =>
  ownerKind === 'platform' || ownerKind === 'company'
    ? queryPolicy.stable.staleTime
    : queryPolicy.list.staleTime;

export const viewConfigQuery = (id: string, ownerKind: ViewOwnerKind | undefined) =>
  queryOptions({
    queryKey: viewConfigKey(id),
    queryFn: async ({ signal }): Promise<ViewQueryConfig> => {
      const detail = await api<
        { unavailable: true } | { config: Pick<ViewConfig, 'filter' | 'sort'> }
      >('GET', `/api/v1/views/${id}`, { ...(signal ? { signal } : {}) });
      if ('unavailable' in detail) throw new Error(VIEW_UNAVAILABLE);
      return configOf(detail.config);
    },
    staleTime: viewConfigStaleTime(ownerKind),
    retry: (count, error) =>
      !(error instanceof Error && error.message === VIEW_UNAVAILABLE) && retryRead(count, error),
  });

export const isViewUnavailable = (error: unknown) =>
  error instanceof Error && error.message === VIEW_UNAVAILABLE;

/** Умова подання «Мої» без id (клієнти й фікстури до E4a-W1): як `MINE_FILTER` сервера. */
const MINE_FILTER: FilterNode = { field: 'owner', op: 'eq', value: { token: 'me' } };

/** Подання без id: умова — з `scope`, сортування — з `/meta`. */
export const scopeConfig = (view: Pick<MetaView, 'scope' | 'sort'>): ViewQueryConfig => ({
  filter: view.scope === 'mine' ? MINE_FILTER : undefined,
  sort: [view.sort],
});

/** Стартове подання: конфігурація вже в `/meta` (`start.config`) — у кеш, без запиту. */
export function seedStartConfig(client: QueryClient, meta: AppMeta): void {
  const { id, config } = meta.start;
  if (!id || !config || client.getQueryData(viewConfigKey(id))) return;
  client.setQueryData(viewConfigKey(id), configOf(config));
}

/** Конфігурація подання для `loader`: з кешу, `start.config` чи `GET /views/{id}`. */
export function loadViewConfig(
  client: QueryClient,
  meta: AppMeta,
  view: MetaView,
): Promise<ViewQueryConfig> {
  if (!view.id) return Promise.resolve(scopeConfig(view));
  seedStartConfig(client, meta);
  return client.ensureQueryData(viewConfigQuery(view.id, view.ownerKind));
}
